import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Mail, KeyRound, ArrowRight } from "lucide-react";
import { useMemberAuth, formatErr } from "./MemberAuthContext";
import { LOGO_URL } from "../assets/images";
import PasswordInput from "./PasswordInput";

export default function MemberLogin() {
  const { loginGoogle, loginPassword, member } = useMemberAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (member) navigate("/membros", { replace: true });
  }, [member, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginPassword(email, password);
      toast.success("Bem-vindo(a)!");
      navigate("/membros", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex items-center justify-center p-6" data-testid="member-login-page">
      <div className="w-full max-w-md bg-white border border-[hsl(var(--border))] p-10 sm:p-12">
        <a href="/" className="flex items-center gap-3" data-testid="member-login-home-link">
          <img src={LOGO_URL} alt="CIEES" className="h-14 w-14" />
          <div>
            <div className="display-heading text-xl text-[hsl(var(--primary))]">CIEES Portugal</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--muted-foreground))]">Área de sócios</div>
          </div>
        </a>

        <h1 className="display-heading text-3xl mt-10 leading-[1.1]">Entrar como sócio.</h1>
        <span className="accent-rule mt-6 block" />

        <button
          type="button"
          onClick={loginGoogle}
          className="mt-8 w-full inline-flex items-center justify-center gap-3 px-5 py-3 bg-white border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm uppercase tracking-[0.16em] font-medium hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
          data-testid="member-login-google"
        >
          <LogIn className="h-4 w-4" /> Entrar com Google
        </button>

        <div className="my-7 flex items-center gap-3">
          <div className="flex-1 h-px bg-[hsl(var(--border))]" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">ou</div>
          <div className="flex-1 h-px bg-[hsl(var(--border))]" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="member-login-form">
          <Field icon={Mail} label="Email">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              data-testid="member-login-email"
            />
          </Field>
          <Field icon={KeyRound} label="Palavra-passe">
            <PasswordInput
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              testIdPrefix="member-login-password"
            />
          </Field>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="member-login-submit">
            {loading ? "A entrar…" : "Entrar"}
          </button>
        </form>

        <div className="mt-7 flex items-center justify-between text-sm">
          <Link to="/membros/registar" className="text-[hsl(var(--primary))] hover:underline" data-testid="member-link-register">
            Ainda não sou sócio →
          </Link>
          <Link to="/membros/esqueci" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="member-link-forgot">
            Esqueci a palavra-passe
          </Link>
        </div>

        <a href="/" className="mt-10 block text-xs uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="member-back-home">
          ← Voltar ao site
        </a>
      </div>
      <style>{`
        .form-input { width:100%; background:white; border:1px solid hsl(var(--border)); padding:10px 12px 10px 36px; font-family:"Outfit", sans-serif; font-size:15px; }
        .form-input:focus { outline:none; border-color: hsl(var(--accent)); }
      `}</style>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">{label}</label>
      <div className="relative">
        <Icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
        {children}
      </div>
    </div>
  );
}
